import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FolderOpen, Loader2, Plus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui';
import { toast } from 'components/ui/use-toast';
import * as CollectionsModule from 'modules/collections';
import { useAdaptTemplate, useUseTemplate } from 'modules/templates';
import { Textarea } from 'components/ui/textarea';

const NEW_COLLECTION = '__new__';
const MIN_TITLE = 3;
const MIN_TOPIC = 3;
const AUTO = 'auto';
const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
type Difficulty = (typeof DIFFICULTIES)[number];
/** Codes the HTTP interceptor turns into the upgrade dialog (services/http). */
const UPGRADE_CODES = new Set(['USAGE_LIMIT_EXCEEDED', 'FEATURE_NOT_ENTITLED']);

export interface UseTemplateTarget {
  id: string;
  title: string;
  /** 'copy' = the template's own questions; 'adapt' = AI-written questions on a topic. */
  mode?: 'copy' | 'adapt';
}

interface UseTemplateDialogProps {
  target: UseTemplateTarget | null;
  onClose: () => void;
}

/**
 * Names the copy and files it in a collection (collections are what teams get
 * access through, same as the blank "Create test" flow), then opens its settings.
 */
export function UseTemplateDialog({ target, onClose }: UseTemplateDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { collections, isLoading: collectionsLoading } = CollectionsModule.Hooks.useOwned({ enabled: Boolean(target) });
  const createCollection = CollectionsModule.Hooks.useCreate();
  const addItem = CollectionsModule.Hooks.useAddItem();
  const useTemplate = useUseTemplate();
  const adaptTemplate = useAdaptTemplate();
  const adapting = target?.mode === 'adapt';
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty | typeof AUTO>(AUTO);

  const [title, setTitle] = useState('');
  const [collectionChoice, setCollectionChoice] = useState('');
  const [newCollectionName, setNewCollectionName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) {
      // Adapt names the test after the topic server-side unless the author types a title.
      setTitle(target.mode === 'adapt' ? '' : target.title);
      setTopic('');
      setDifficulty(AUTO);
      setCollectionChoice('');
      setNewCollectionName('');
      setSubmitting(false);
    }
  }, [target]);

  const creatingNew = collectionChoice === NEW_COLLECTION;
  const titleOk = adapting ? title.trim().length === 0 || title.trim().length >= MIN_TITLE : title.trim().length >= MIN_TITLE;
  const canSubmit =
    titleOk &&
    (!adapting || topic.trim().length >= MIN_TOPIC) &&
    Boolean(collectionChoice) &&
    (!creatingNew || Boolean(newCollectionName.trim()));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target || !canSubmit || submitting) return;
    setSubmitting(true);
    try {
      // Copy first: if it fails (plan limit, permissions) nothing else was created,
      // so a retry can't leave an empty duplicate collection behind.
      const { testId, subjectId } = adapting
        ? await adaptTemplate.mutateAsync({
            id: target.id,
            topic: topic.trim(),
            title: title.trim() || undefined,
            difficulty: difficulty === AUTO ? undefined : difficulty,
          })
        : await useTemplate.mutateAsync({ id: target.id, title: title.trim() });
      // The test exists now; a failed filing must not hide it from the author.
      try {
        let collectionId = collectionChoice;
        if (creatingNew) {
          const created = await createCollection.mutateAsync({ name: newCollectionName.trim() });
          if (!created?.id) throw new Error('collection-create-failed');
          collectionId = created.id;
        }
        await addItem.mutateAsync({ collectionId, type: 'test', itemId: testId });
      } catch {
        toast.warning(t('templates.created_not_filed'));
      }
      toast.success(t('templates.created'));
      onClose();
      // Exam tests (no subject copy) open on their sections, like a newly created IELTS test.
      navigate(`/app/tests/${testId}/${subjectId ? 'settings' : 'questions'}`);
    } catch (error) {
      // Plan errors already open the global upgrade dialog (HTTP interceptor); report everything else here.
      const response = (error as { response?: { data?: { code?: string; message?: string } } })?.response;
      const data = response?.data;
      if (!data?.code || !UPGRADE_CODES.has(data.code)) {
        // No response on a long AI request usually means a proxy timeout while the server kept going.
        const description = !response && adapting ? t('templates.adapt_check_tests') : data?.message;
        toast.error(t('templates.use_failed'), { description });
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(target)} onOpenChange={(open) => !open && !submitting && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{adapting ? t('templates.adapt_title') : t('templates.use_title')}</DialogTitle>
            <DialogDescription>{adapting ? t('templates.adapt_desc') : t('templates.use_desc')}</DialogDescription>
          </DialogHeader>

          <div className="mt-5 space-y-4">
            {adapting ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="template-adapt-topic">{t('templates.adapt_topic')}</Label>
                  <Textarea
                    id="template-adapt-topic"
                    rows={2}
                    value={topic}
                    maxLength={200}
                    placeholder={t('templates.adapt_topic_placeholder')}
                    onChange={(e) => setTopic(e.target.value)}
                    autoFocus
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-adapt-difficulty">{t('templates.adapt_difficulty')}</Label>
                  <Select value={difficulty} onValueChange={(v) => setDifficulty(v as Difficulty | typeof AUTO)}>
                    <SelectTrigger id="template-adapt-difficulty">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AUTO}>{t('templates.adapt_difficulty_auto')}</SelectItem>
                      {DIFFICULTIES.map((d) => (
                        <SelectItem key={d} value={d}>
                          {t(`templates.difficulty_${d}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="template-test-title">
                {t('create_test.name_label')}
                {adapting ? <span className="font-normal text-muted-foreground"> · {t('auth.ob_optional')}</span> : null}
              </Label>
              <Input
                id="template-test-title"
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus={!adapting}
                required={!adapting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-test-collection">{t('create_test.collection_label')}</Label>
              <Select value={collectionChoice} onValueChange={setCollectionChoice}>
                <SelectTrigger id="template-test-collection">
                  <SelectValue placeholder={t('create_test.select_collection')} />
                </SelectTrigger>
                <SelectContent>
                  {collections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={NEW_COLLECTION}>
                    <span className="flex items-center gap-2">
                      <Plus className="h-3.5 w-3.5" /> {t('create_test.new_collection')}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              {collectionsLoading ? <p className="text-xs text-muted-foreground">{t('loading')}</p> : null}
              {creatingNew ? (
                <div className="flex items-center gap-2 pt-1">
                  <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  <Input
                    aria-label={t('create_test.new_collection_placeholder')}
                    placeholder={t('create_test.new_collection_placeholder')}
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                  />
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter className="mt-6 gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit || submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {adapting ? t('templates.adapt_working') : t('templates.creating')}
                </>
              ) : adapting ? (
                t('templates.adapt_action')
              ) : (
                t('templates.use')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
